import { JSX, useMemo } from "react";
import { useTranslation } from "react-i18next";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { ListInfo, SelectState } from "utils/list/List";
import { asTitle } from "utils/Utils";

export type TableActionbarProps = {
	listInfo: ListInfo<unknown>;

	onMoveUp: () => void;
	onMoveDown: () => void;
	onDuplicate?: () => void;
	onDelete: () => void;
};

export default function TableActionbar(
	props: Readonly<TableActionbarProps>
): JSX.Element {
	const { t } = useTranslation();
	const selectState = useMemo(
		() => props.listInfo.toSelectState(),
		[props.listInfo]
	);

	return (
		<>
			<Tooltip title={asTitle(t("up"))}>
				<span>
					<IconButton
						aria-label="up"
						disabled={
							selectState === SelectState.NONE ||
							(selectState & SelectState.FIRST) !== 0
						}
						onClick={props.onMoveUp}
						size="large">
						<ArrowUpwardIcon />
					</IconButton>
				</span>
			</Tooltip>
			<Tooltip title={asTitle(t("down"))}>
				<span>
					<IconButton
						aria-label="down"
						disabled={
							selectState === SelectState.NONE ||
							(selectState & SelectState.LAST) !== 0
						}
						onClick={props.onMoveDown}
						size="large">
						<ArrowDownwardIcon />
					</IconButton>
				</span>
			</Tooltip>
			{props.onDuplicate && (
				<Tooltip title={asTitle(t("duplicate"))}>
					<span>
						<IconButton
							aria-label="duplicate"
							disabled={selectState === SelectState.NONE}
							onClick={props.onDuplicate}
							size="large">
							<ContentCopyIcon />
						</IconButton>
					</span>
				</Tooltip>
			)}
			<Tooltip title={asTitle(t("delete"))}>
				<span>
					<IconButton
						aria-label="delete"
						disabled={selectState === SelectState.NONE}
						onClick={props.onDelete}
						size="large">
						<DeleteIcon />
					</IconButton>
				</span>
			</Tooltip>
		</>
	);
}
